import { createClient } from '@supabase/supabase-js'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'https://ciqowbqbtaluohujwiil.supabase.co'
// Use the anon JWT token for client-side authentication (safe for browsers)
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImNpcW93YnFidGFsdW9odWp3aWlsIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjIwNjMwMjMsImV4cCI6MjA3NzYzOTAyM30.NE2zDKTw7IaaYZm_xx4Bed-wqEhlP9pRtPwaL0yRVHI'

if (!supabaseUrl || !supabaseAnonKey) {
  console.error('Supabase configuration missing. Please check your credentials.')
}

console.log('Supabase config:', { url: supabaseUrl, keyPrefix: supabaseAnonKey.substring(0, 20) + '...' })

export const supabase = createClient(supabaseUrl, supabaseAnonKey)

// Auth helper functions
export const auth = {
  // Sign up new user
  async signUp(email: string, password: string, userData: { name: string; faction?: string }) {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          name: userData.name,
          faction: userData.faction || 'Imperial',
          level: 1
        }
      }
    })
    return { data, error }
  },

  // Sign in existing user
  async signIn(email: string, password: string) {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    })
    return { data, error }
  },

  // Sign out
  async signOut() {
    const { error } = await supabase.auth.signOut()
    return { error }
  },

  // Get current user
  async getCurrentUser() {
    const { data: { user }, error } = await supabase.auth.getUser()
    return { user, error }
  },

  // Listen to auth changes
  onAuthStateChange(callback: (event: string, session: any) => void) {
    return supabase.auth.onAuthStateChange(callback)
  }
}

// Database helper functions
export const db = {
  // Create user profile after registration
  async createUserProfile(userId: string, userData: { name: string; email: string; faction?: string }) {
    const { data, error } = await supabase
      .from('profiles')
      .insert([
        {
          id: userId,
          username: userData.name,
          email: userData.email,
          civilization_name: userData.faction || 'Terran Empire',
          race: 'human',
          level: 1,
          credits: 10000
        }
      ])
    return { data, error }
  },

  // Get user profile
  async getUserProfile(userId: string) {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single()
    return { data, error }
  },

  // Update user profile
  async updateUserProfile(userId: string, updates: any) {
    const { data, error } = await supabase
      .from('profiles')
      .update(updates)
      .eq('id', userId)
    return { data, error }
  },

  // Get user fleets
  async getUserFleets(userId: string) {
    const { data, error } = await supabase
      .from('fleets')
      .select('*')
      .eq('owner_id', userId)
    return { data, error }
  },

  // Get user planets
  async getUserPlanets(userId: string) {
    const { data, error } = await supabase
      .from('planets')
      .select('*')
      .eq('owner_id', userId)
    return { data, error }
  },

  // Get dashboard statistics
  async getDashboardStats(userId: string) {
    try {
      // Get user profile with current stats
      const profileResult = await this.getUserProfile(userId)
      
      // Get fleet count
      const fleetResult = await supabase
        .from('fleets')
        .select('id')
        .eq('user_id', userId)
      
      // Get planet count  
      const planetResult = await supabase
        .from('planets')
        .select('id')
        .eq('owner_id', userId)
        
      // Get research count
      const researchResult = await supabase
        .from('research')
        .select('id')
        .eq('user_id', userId)
        .eq('completed', true)

      const profile = profileResult.data
      const fleetCount = fleetResult.data?.length || 0
      const planetCount = planetResult.data?.length || 1
      const researchCount = researchResult.data?.length || 0

      return {
        data: {
          credits: profile?.credits || 10000,
          energy: profile?.energy || 1000,
          minerals: profile?.minerals || 1000,
          level: profile?.level || 1,
          experience: profile?.experience_points || 0,
          ships: fleetCount,
          planets: planetCount,
          research: researchCount,
          influence: Math.floor((profile?.level || 1) * 50 + researchCount * 10)
        },
        error: null
      }
    } catch (error) {
      return { data: null, error }
    }
  },

  // Get recent notifications
  async getNotifications(userId: string, limit: number = 10) {
    const { data, error } = await supabase
      .from('notifications')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(limit)
    return { data, error }
  },

  // Create welcome notifications for new users
  async createWelcomeNotifications(userId: string) {
    const welcomeNotifications = [
      {
        user_id: userId,
        type: 'welcome',
        message: 'Welcome to Crown of Suns! Your empire awaits your command.',
        urgent: false
      },
      {
        user_id: userId,
        type: 'info',
        message: 'Start by exploring the galaxy and building your first fleet.',
        urgent: false
      },
      {
        user_id: userId,
        type: 'info',
        message: 'Research new technologies to advance your civilization.',
        urgent: false
      }
    ]

    const { data, error } = await supabase
      .from('notifications')
      .insert(welcomeNotifications)
    return { data, error }
  }
}